import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Play, ArrowLeft, Gamepad2, Target, Star, Zap, Lock, Heart } from 'lucide-react'
import { useEffect, useState } from 'react'
import { auth } from '@/config/firebase'
import { getUserSubscription, isPremiumSubscription } from '@/services/subscriptionService'

export default function PollutionPatrolInfoPage() {
  const [isPremium, setIsPremium] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const checkAccess = async () => {
      const user = auth.currentUser
      if (user) {
        const subscription = await getUserSubscription(user.uid)
        setIsPremium(isPremiumSubscription(subscription))
      }
      setLoading(false)
    }
    checkAccess()
  }, [])

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back Button */}
      <Link to="/" className="inline-flex items-center text-dark-navy/70 hover:text-dark-navy mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Games
      </Link>

      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="inline-block relative">
          <div className="text-6xl mb-4">♻️</div>
          {!loading && !isPremium && (
            <div className="absolute -top-2 -right-8 bg-medium-purple text-white text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Premium
            </div>
          )}
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-dark-navy mb-4">Pollution Patrol</h1>
        <p className="text-xl text-dark-navy/70 max-w-2xl mx-auto">
          Join Thea on a mission to clean up the ocean! Jump across platforms, collect trash, 
          and rescue baby sea creatures in this exciting platformer adventure.
        </p>
        
        {/* Play/Unlock Button */}
        <div className="mt-8">
          {loading ? (
            <div className="inline-flex items-center justify-center px-8 py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-medium-purple border-t-transparent"></div>
            </div>
          ) : isPremium ? (
            <Link to="/pollution-patrol/play">
              <Button size="lg" className="text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-medium-teal hover:bg-light-teal text-white">
                <Play className="w-5 h-5 mr-2" />
                Start Playing
              </Button>
            </Link>
          ) : (
            <Link to="/subscription">
              <Button size="lg" className="text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-medium-purple hover:bg-deep-purple text-white">
                <Lock className="w-5 h-5 mr-2" />
                Unlock Premium
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Screenshot Section */}
      <div className="mb-12">
        <div className="bg-gradient-to-br from-medium-teal/80 to-dark-navy/90 rounded-2xl overflow-hidden shadow-xl aspect-video flex items-center justify-center">
          <img 
            src="/screenshots/pollution_patrol.jpg" 
            alt="Pollution Patrol gameplay screenshot"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none'
              e.currentTarget.nextElementSibling?.classList.remove('hidden')
            }}
          />
          <div className="hidden text-center p-8">
            <Gamepad2 className="w-16 h-16 text-white/50 mx-auto mb-4" />
            <p className="text-white/70">Screenshot coming soon!</p>
          </div>
        </div>
      </div>

      {/* Quick Start Guide */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6 flex items-center gap-2">
          <Target className="w-6 h-6 text-medium-teal" />
          How to Play
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">⬆️</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Jump</h3>
              <p className="text-dark-navy/70 text-sm">Press SPACE, UP arrow, or tap to jump</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">🔄</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Double Jump</h3>
              <p className="text-dark-navy/70 text-sm">Jump again in mid-air to reach higher platforms!</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">⬅️➡️</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Move</h3>
              <p className="text-dark-navy/70 text-sm">Use arrow keys to move left and right</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-4 bg-white/50 rounded-xl">
            <span className="text-2xl">🐚</span>
            <div>
              <h3 className="font-semibold text-dark-navy">Collect</h3>
              <p className="text-dark-navy/70 text-sm">Gather shells and pearls for points</p>
            </div>
          </div>
        </div>
      </div>

      {/* Rescue System */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6 flex items-center gap-2">
          <Heart className="w-6 h-6 text-bright-coral" />
          Rescue Baby Sea Creatures
        </h2>
        
        <div className="space-y-4">
          <p className="text-dark-navy/80">
            Throughout your adventure, you'll find baby sea creatures who've been separated from their parents. 
            Rescue them and they'll follow you on your journey!
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-gradient-to-br from-pink-100/50 to-red-100/50 rounded-xl border border-pink-200">
              <h3 className="font-bold text-pink-700 mb-2">🐢 Find Babies</h3>
              <p className="text-sm text-dark-navy/70">Look for baby turtles, fish, and other creatures waiting to be rescued.</p>
            </div>
            
            <div className="p-4 bg-gradient-to-br from-green-100/50 to-teal-100/50 rounded-xl border border-green-200">
              <h3 className="font-bold text-green-700 mb-2">💕 Reunite Families</h3>
              <p className="text-sm text-dark-navy/70">Lead babies back to their parents for a +50 bonus reward!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Power-ups & Hazards */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6 flex items-center gap-2">
          <Star className="w-6 h-6 text-medium-teal" />
          Power-ups & Hazards
        </h2>
        
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-dark-navy text-lg mb-3 text-green-600">✨ Power-ups</h3>
            <ul className="space-y-2 text-dark-navy/80">
              <li className="flex items-center gap-2">
                <span className="text-xl">⚡</span>
                <span><strong>Speed Boost</strong> – Move faster for a limited time!</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-xl">🛡️</span>
                <span><strong>Bubble Shield</strong> – Protects you from one hazard hit.</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold text-dark-navy text-lg mb-3 text-red-600">⚠️ Hazards</h3>
            <ul className="space-y-2 text-dark-navy/80">
              <li className="flex items-center gap-2">
                <span className="text-xl">🪼</span>
                <span><strong>Jellyfish</strong> – Stinging danger! Avoid at all costs.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-xl">🦔</span>
                <span><strong>Sea Urchins</strong> – Spiky creatures on the ocean floor.</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-xl">🥤</span>
                <span><strong>Ocean Trash</strong> – Pollution hazards to avoid.</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold text-dark-navy text-lg mb-3">Pro Tips</h3>
            <ul className="space-y-2 text-dark-navy/80">
              <li className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-bright-coral" />
                <span>You have 3 lives – be careful around hazards!</span>
              </li>
              <li className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-bright-coral" />
                <span>Double-jump is key to reaching high platforms.</span>
              </li>
              <li className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-bright-coral" />
                <span>Don't fall off the platforms!</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Scoring */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6">Scoring</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-medium-teal/10 rounded-xl">
            <span className="text-2xl">🐚</span>
            <p className="font-bold text-medium-teal mt-1">+10</p>
            <p className="text-xs text-dark-navy/60">Shell</p>
          </div>
          <div className="text-center p-4 bg-medium-purple/10 rounded-xl">
            <span className="text-2xl">💎</span>
            <p className="font-bold text-medium-purple mt-1">+5</p>
            <p className="text-xs text-dark-navy/60">Pearl</p>
          </div>
          <div className="text-center p-4 bg-pink-100/50 rounded-xl">
            <span className="text-2xl">🐢</span>
            <p className="font-bold text-pink-600 mt-1">+25</p>
            <p className="text-xs text-dark-navy/60">Rescue Baby</p>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-yellow-100/50 to-orange-100/50 rounded-xl">
            <span className="text-2xl">💕</span>
            <p className="font-bold text-orange-600 mt-1">+50</p>
            <p className="text-xs text-dark-navy/60">Reunite Family</p>
          </div>
        </div>
      </div>

      {/* Difficulty Levels */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-8 border border-white/40">
        <h2 className="text-2xl font-bold text-dark-navy mb-6">Difficulty Levels</h2>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-green-100/50 rounded-xl border-2 border-green-200">
            <h3 className="font-bold text-green-700 mb-2">🌱 Easy</h3>
            <p className="text-sm text-dark-navy/70">Wider platforms, fewer hazards. Perfect for learning the ropes!</p>
          </div>
          
          <div className="p-4 bg-yellow-100/50 rounded-xl border-2 border-yellow-200">
            <h3 className="font-bold text-yellow-700 mb-2">🌿 Medium</h3>
            <p className="text-sm text-dark-navy/70">Balanced challenge with more obstacles and trickier jumps.</p>
          </div>
          
          <div className="p-4 bg-red-100/50 rounded-xl border-2 border-red-200">
            <h3 className="font-bold text-red-700 mb-2">🔥 Hard</h3>
            <p className="text-sm text-dark-navy/70">Smaller platforms, more hazards. For true ocean heroes!</p>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="text-center py-8">
        {isPremium ? (
          <Link to="/pollution-patrol/play">
            <Button size="lg" className="text-lg px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-medium-teal hover:bg-light-teal text-white">
              <Play className="w-5 h-5 mr-2" />
              Start Your Mission!
            </Button>
          </Link>
        ) : (
          <div className="space-y-4">
            <Link to="/subscription">
              <Button size="lg" className="text-lg px-10 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-medium-purple hover:bg-deep-purple text-white">
                <Lock className="w-5 h-5 mr-2" />
                Unlock Premium to Play
              </Button>
            </Link>
            <p className="text-dark-navy/60 text-sm">
              Premium includes all games, character creator, and supports ocean conservation!
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
